import React, { useState } from 'react';
import { useGetPaymentReportsQuery, useGetSettingsQuery } from '../redux/api/roomBookingApi';

const PaymentReport = () => {
    // State for Filters
    const [filters, setFilters] = useState({
        startDate: new Date().toISOString().split('T')[0], // Default Today
        endDate: new Date().toISOString().split('T')[0],
        category: 'All', // All, Donation, Seva, Guru Seva, Room Booking
        mode: 'All' // All, Online, Offline
    });

    const [triggerSearch, setTriggerSearch] = useState(false);

    // Fetch Settings
    const { data: settingsData } = useGetSettingsQuery();
    const settings = settingsData?.data || {};

    // Fetch Data
    const { data: reportData, isLoading, isError, refetch } = useGetPaymentReportsQuery(filters, {
        skip: false, // Always fetch initially or controlled? Let's fetch initially.
        categoryId: filters.category, // Pass to query params
        refetchOnMountOrArgChange: true
    });

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        // refetch handled by hook via arg change
    };

    const printReport = () => {
        window.print();
    };

    // Calculate Totals
    const totalAmount = reportData?.summary?.total_amount || 0;
    const totalTxns = reportData?.summary?.total_transactions || 0;

    return (
        <div className="space-top space-extra-bottom">
            <div className="container">
                {/* Title & Filters */}
                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 text-white">Admin Payment Report</h4>
                        <button className="btn btn-light btn-sm" onClick={printReport}>Print Report</button>
                    </div>
                    <div className="card-body bg-light">
                        <form onSubmit={handleSearch} className="row g-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Start Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    required
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label fw-bold">End Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    name="endDate"
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    required
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-bold">Category</label>
                                <select className="form-select" name="category" value={filters.category} onChange={handleFilterChange}>
                                    <option value="All">All Categories</option>
                                    {settings.enable_donations && <option value="Donation">Donations</option>}
                                    {settings.enable_online_sevas && <option value="Seva">Sevas</option>}
                                    {settings.enable_guru_seva && <option value="Guru Seva">Guru Sevas</option>}
                                    <option value="Room Booking">Room Booking</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label fw-bold">Payment Mode</label>
                                <select className="form-select" name="mode" value={filters.mode} onChange={handleFilterChange}>
                                    <option value="All">All Modes</option>
                                    <option value="Online">Online</option>
                                    <option value="Offline">Offline / Cash</option>
                                </select>
                            </div>
                            {/* <div className="col-md-2">
                <button type="submit" className="btn btn-primary w-100">Generate</button>
              </div> */}
                        </form>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4 text-center">
                    <div className="col-md-4">
                        <div className="card bg-primary text-white p-3">
                            <h5>Total Transactions</h5>
                            <h3>{isLoading ? '...' : totalTxns}</h3>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card bg-success text-white p-3">
                            <h5>Total Amount</h5>
                            <h3>₹ {isLoading ? '...' : totalAmount}</h3>
                        </div>
                    </div>
                    {/* Placeholder for Online vs Offline stats if computed frontend side 
           <div className="col-md-4">
             <div className="card bg-info text-white p-3">
               <h5>Filtered View</h5>
               <small>{filters.category} - {filters.mode}</small>
             </div>
           </div> */}
                </div>

                {/* Report Table */}
                <div className="card shadow-sm">
                    <div className="card-body">
                        {isLoading && <div className="text-center py-5">Generating Report...</div>}
                        {isError && <div className="text-center py-5 text-danger">Error fetching report data.</div>}

                        {!isLoading && !isError && reportData?.data && (
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Date</th>
                                            <th>Booking ID</th>
                                            <th>Devotee Name</th>
                                            <th>Phone</th>
                                            <th>Type</th>
                                            <th>Details / Sub-Type</th>
                                            <th>Mode</th>
                                            <th className="text-end">Amount (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.data.length > 0 ? (
                                            reportData.data.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td>{new Date(row.date).toLocaleDateString()}</td>
                                                    <td><small>{row.booking_id}</small></td>
                                                    <td>{row.name}</td>
                                                    <td>{row.phone}</td>
                                                    <td>
                                                        <span className={`badge ${row.type === 'Donation' ? 'bg-info' :
                                                            row.type === 'Room Booking' ? 'bg-warning text-dark' : 'bg-secondary'
                                                            }`}>
                                                            {row.type}
                                                        </span>
                                                    </td>
                                                    <td><small>{row.sub_type}</small></td>
                                                    <td>
                                                        <span className={`badge ${row.mode === 'Online' ? 'bg-success' : 'bg-dark'}`}>
                                                            {row.mode}
                                                        </span>
                                                    </td>
                                                    <td className="text-end fw-bold">{row.amount}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4">No records found for the selected criteria.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                    {reportData.data.length > 0 && (
                                        <tfoot>
                                            <tr className="table-secondary fw-bold">
                                                <td colSpan="7" className="text-end">Grand Total:</td>
                                                <td className="text-end">₹ {totalAmount}</td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default PaymentReport;
